import React, { useContext } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { spacing16, backgroundColorLevelCurrent } from "@churchofjesuschrist/eden-style-constants";
import OverflowWrapperContext from "./OverflowWrapper/Context";
import StickyHeaderContext from "./Table/StickyHeaderContext";
import { backgroundDef as fadeBackgroundDef, topBackground as fadeBackground, size as fadeSize } from "@churchofjesuschrist/eden-fade";

/*
The relative position is because the "*HiddenMarkers" from ColumnHiding need
position:absolute.

When "stuck":
- The ::before is adding the bottom shadow
- The ::after and z-indexing are to cover up the OverflowWrapper top shadow

All the `hasSorting` logic can probably be replaced with a :has selector once
FF has support.

See Table CSS for the --eden-table-density-divisor custom property.
*/
import { jsx as _jsx } from "react/jsx-runtime";
const Root = styled.th.withConfig({
  componentId: "sc-arbpvo-0"
})(["", ";position:relative;text-align:", ";padding:calc(", " / var(--eden-table-density-divisor,1));&[scope=\"col\"]{z-index:1;&::after{content:\"\";position:absolute;top:1px;left:0px;bottom:1px;right:0px;background-color:", ";z-index:-1;}", "}"], fadeBackgroundDef, _ref => {
  let {
    dataType
  } = _ref;
  return dataType === "number" ? "end" : "start";
}, spacing16, backgroundColorLevelCurrent, _ref2 => {
  let {
    stuck
  } = _ref2;
  return stuck ? `&::before {
        content: "";
        pointer-events: none;
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        height: ${fadeSize};
        // Matches top shadow from OverflowWrapper
        background: ${fadeBackground};
      }` : "";
});

/** Represents a header cell in a table. */
export default function TH(_ref3) {
  let {
    children,
    scope,
    type,
    ...props
  } = _ref3;
  const {
    ariaSort,
    ...rest
  } = props;
  const tableOverflowing = useContext(OverflowWrapperContext);
  const stickyHeader = useContext(StickyHeaderContext);
  return /*#__PURE__*/_jsx(Root, {
    ...rest,
    scope: scope,
    role: scope === "col" ? "columnheader" : "rowheader",
    "aria-sort": ariaSort,
    dataType: type,
    stuck: stickyHeader && tableOverflowing,
    children: children
  });
}
TH.propTypes = {
  /** The header cell contents. */
  children: PropTypes.node,
  /** Specifies which cells the header cell applies to. */
  scope: PropTypes.oneOf(["col", "colgroup", "row", "rowgroup"]).isRequired,
  /** The data type of the cell where "controls" would be buttons, form fields, etc. Formatting, such as spacing and text alignment, is automatically applied. */
  type: PropTypes.oneOf(["controls", "normal", "number"])

  /** INTERNAL PROPS (See cellDecorator of Sorting/Sorting) */

  /** The sort direction if the column is currently sorted. */
  // ariaSort: PropTypes.oneOf(["ascending", "descending"]),
};